/**
 * ChessClock - Manages timed chess games
 * Tracks time for both player and opponent
 */

import { ChessClockData } from '../types';

export class ChessClock {
  private playerTimeMs: number;
  private opponentTimeMs: number;
  private isPlayerTurn: boolean;
  private isPaused: boolean;
  private totalTimeMs: number;

  private lastUpdateTime: number;
  private onPlayerTimeout?: () => void;
  private onOpponentTimeout?: () => void;
  private onTick?: (data: ChessClockData) => void;

  /**
   * Create a chess clock
   * @param totalTimeMs Total time for each side in milliseconds
   * @param isPlayerTurn Whether it's the player's turn initially
   */
  constructor(totalTimeMs: number, isPlayerTurn: boolean = true) {
    this.playerTimeMs = totalTimeMs;
    this.opponentTimeMs = totalTimeMs;
    this.totalTimeMs = totalTimeMs;
    this.isPlayerTurn = isPlayerTurn;
    this.isPaused = true; // Start paused
    this.lastUpdateTime = Date.now();
  }

  /**
   * Start the clock
   */
  start(): void {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.lastUpdateTime = Date.now();
  }

  /**
   * Pause the clock
   */
  pause(): void {
    if (this.isPaused) return;
    this.update(); // Update one last time before pausing
    this.isPaused = true;
  }

  /**
   * Resume the clock
   */
  resume(): void {
    this.start();
  }

  /**
   * Switch whose turn it is
   */
  switchTurn(): void {
    this.update(); // Update time for current player
    this.isPlayerTurn = !this.isPlayerTurn;
    this.lastUpdateTime = Date.now();
  }

  /**
   * Update the clock (should be called regularly, e.g., every frame)
   */
  update(): void {
    if (this.isPaused) return;

    const now = Date.now();
    const elapsedMs = now - this.lastUpdateTime;
    this.lastUpdateTime = now;

    if (this.isPlayerTurn) {
      this.playerTimeMs -= elapsedMs;
      if (this.playerTimeMs <= 0) {
        this.playerTimeMs = 0;
        this.isPaused = true;
        if (this.onPlayerTimeout) {
          this.onPlayerTimeout();
        }
      }
    } else {
      this.opponentTimeMs -= elapsedMs;
      if (this.opponentTimeMs <= 0) {
        this.opponentTimeMs = 0;
        this.isPaused = true;
        if (this.onOpponentTimeout) {
          this.onOpponentTimeout();
        }
      }
    }

    // Call tick callback
    if (this.onTick) {
      this.onTick(this.getData());
    }
  }

  /**
   * Add time to player's clock
   */
  addPlayerTime(ms: number): void {
    this.playerTimeMs += ms;
  }

  /**
   * Add time to opponent's clock
   */
  addOpponentTime(ms: number): void {
    this.opponentTimeMs += ms;
  }

  /**
   * Set callback for when player runs out of time
   */
  setOnPlayerTimeout(callback: () => void): void {
    this.onPlayerTimeout = callback;
  }

  /**
   * Set callback for when opponent runs out of time
   */
  setOnOpponentTimeout(callback: () => void): void {
    this.onOpponentTimeout = callback;
  }

  /**
   * Set callback for each tick (called during update)
   */
  setOnTick(callback: (data: ChessClockData) => void): void {
    this.onTick = callback;
  }

  /**
   * Get current clock data
   */
  getData(): ChessClockData {
    return {
      playerTimeMs: this.playerTimeMs,
      opponentTimeMs: this.opponentTimeMs,
      isPlayerTurn: this.isPlayerTurn,
      isPaused: this.isPaused,
      totalTimeMs: this.totalTimeMs
    };
  }

  /**
   * Get player's remaining time in milliseconds
   */
  getPlayerTime(): number {
    return this.playerTimeMs;
  }

  /**
   * Get opponent's remaining time in milliseconds
   */
  getOpponentTime(): number {
    return this.opponentTimeMs;
  }

  /**
   * Check if player has time remaining
   */
  hasPlayerTimeRemaining(): boolean {
    return this.playerTimeMs > 0;
  }

  /**
   * Check if opponent has time remaining
   */
  hasOpponentTimeRemaining(): boolean {
    return this.opponentTimeMs > 0;
  }

  /**
   * Format time as MM:SS
   */
  static formatTime(ms: number): string {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format time as MM:SS.mmm (with milliseconds)
   */
  static formatTimeDetailed(ms: number): string {
    const totalMs = Math.max(0, ms);
    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const milliseconds = totalMs % 1000;
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  }

  /**
   * Destroy the clock and clean up
   */
  destroy(): void {
    this.pause();
    this.onPlayerTimeout = undefined;
    this.onOpponentTimeout = undefined;
    this.onTick = undefined;
  }
}
